/**
 * The flight scene's half of AD-012's propeller effect: building the discs
 * once the palette is in hand, and collecting each frame's sources.
 *
 * Split out of `flight-scene.ts` for the reason `docs/engineering/lessons.md`
 * records for `tunables.ts` — the host file sits close to aislop's size ceiling
 * and a cohesive addition belongs in a sibling rather than squeezed in by
 * trimming the host's provenance comments. `flight-scene.ts` keeps the two
 * call sites (one in `watchInit`, one in the frame loop) and the field.
 *
 * The host is described structurally rather than by importing `SceneState`,
 * which is private to `flight-scene.ts`: this module needs four of its fields
 * and none of its lifecycle.
 */

import type { Pose, SimState } from "@red-opus/engine";
import type * as THREE from "three/webgpu";
import { shadingPalette } from "./aircraft-mesh.ts";
import type { EffectsSettings } from "./effects-settings.ts";
import { PLAYER_ID } from "./encounter-setup.ts";
import { propellerDriveOf } from "./encounter-views.ts";
import { createPropellerDiscs, PROP_RADIUS_COCKPIT_SCALE, type PropellerDiscs, type PropellerSource } from "./propeller-discs.ts";
import { eyeOffsetFor } from "./cockpit-view.ts";
import { spinModelledPropeller } from "./propeller-blade-spin.ts";
import { propellerRpmFraction } from "./propeller-rpm.ts";
import type { EncounterRunner } from "./sim-runner.ts";

// Re-exported so `flight-scene.ts` takes the whole propeller wiring — the
// functions, the handle type it stores, and the default settings it falls back
// to — from this one import rather than four.
export { DEFAULT_EFFECTS_SETTINGS } from "./effects-settings.ts";
export type { EffectsSettings } from "./effects-settings.ts";
export type { PropellerDiscs } from "./propeller-discs.ts";

/** What `flight-scene.ts`'s own state has to offer for the discs to be wired up. */
export interface PropellerHost {
  readonly scene: THREE.Scene;
  readonly runner: EncounterRunner;
  readonly playerModelId: string;
  /**
   * The player's own pose group. Read here for the *other* propeller: on a D.H.2
   * or an F.E.2b the effect draws nothing and the `.glb`'s modelled blade is the
   * propeller, so it is the thing that has to turn
   * (`propeller-blade-spin.ts`).
   */
  readonly aircraft: THREE.Group;
  readonly effects: EffectsSettings;
  /** Written here once the discs exist; `null` until then, and for good if the palette never loads. */
  propellers: PropellerDiscs | null;
  readonly disposed: boolean;
}

/**
 * Builds the discs once `BARON.PAL` is in hand and adds them to the scene.
 *
 * A palette that never loads costs the propeller effect and nothing else,
 * which is the same "skip what fails to load" policy `aircraft-mesh.ts`
 * applies to the ramp shading that needs the same file.
 */
export async function loadPropellers(host: PropellerHost): Promise<void> {
  const palette = await shadingPalette();
  if (!palette || host.disposed) return;
  const discs = createPropellerDiscs(palette);
  discs.setEnabled(host.effects.propellerEffect);
  host.propellers = discs;
  host.scene.add(discs.group);
}

/**
 * This frame's propeller sources: the player's own aeroplane plus every other
 * one in the sky, each with the pose it is being *drawn* at and the rate its
 * engine is actually turning at (`propeller-rpm.ts`).
 *
 * The player's own rate is derived here from the same `SimState` the audio
 * engine is fed a line later in the frame; every opponent's arrives already
 * derived on its `AircraftView`, beside its interpolated pose.
 */
export function propellerSourcesFor(host: PropellerHost, pose: Pose, simState: SimState): PropellerSource[] {
  const sources: PropellerSource[] = [
    {
      id: PLAYER_ID,
      modelId: host.playerModelId,
      pose,
      rpmFraction: propellerRpmFraction(propellerDriveOf(simState)),
      // Only the aeroplane the camera sits inside shrinks — see the field's doc.
      cockpitRadiusScale: PROP_RADIUS_COCKPIT_SCALE,
      // On the sight line, so the quarter disc stays in the windshield.
      cockpitUpM: eyeOffsetFor(host.playerModelId.replace(/^models\//, "")).upM,
    },
  ];
  for (const other of host.runner.others()) {
    sources.push({ id: other.id, modelId: other.modelId, pose: other.pose, rpmFraction: other.propellerRpm ?? 0 });
  }
  return sources;
}

/**
 * The player's own **modelled** propeller, turned by whatever this frame already
 * decided his propeller is doing.
 *
 * Deliberately reads the rate back out of the list above rather than computing
 * `propellerRpmFraction` a second time: the two propellers are two drawings of
 * one turning shaft, and one frame that gave the disc one rate and the blade
 * another would be a bug nobody could see until an aeroplane carried both.
 *
 * Runs whether or not the disc layer exists — on a D.H.2 the layer draws nothing
 * for this aeroplane either way, and on a Camel with PROPELLER EFFECT off there
 * is no layer to read a rate from but there is still a blade on the nose.
 */
export function spinPlayerModelledPropeller(
  host: PropellerHost,
  sources: readonly PropellerSource[],
  dtS: number,
): void {
  const player = sources.find((source) => source.id === PLAYER_ID);
  spinModelledPropeller(host.aircraft, player?.rpmFraction ?? 0, dtS);
}
